export const DEFAULT_QUERY = Object.freeze({
  q: "",
  profiles: Object.freeze([]),
  regions: Object.freeze([]),
  priceMin: null,
  priceMax: null,
  ageMin: null,
  ageMax: null,
  scoreMin: null,
  scoreMax: null,
  availableOnly: false,
  groupByOutturn: true,
  sort: "catalogue",
  view: "list",
});

const VALID_SORT_FIELDS = new Set([
  "review", "code", "name", "distillery", "price", "profile", "region", "abv", "age",
]);
const VALID_SORTS = new Set([
  "catalogue",
  ...[...VALID_SORT_FIELDS].flatMap((field) => [`${field}-asc`, `${field}-desc`]),
]);

const uniqueValues = (values) => [...new Set(values.map((value) => value.trim()).filter(Boolean))];

function numberOrNull(value) {
  if (value == null || String(value).trim() === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function parseQuery(search = "") {
  const params = new URLSearchParams(search);
  const sort = params.get("sort");
  return {
    q: params.get("q")?.trim() ?? "",
    profiles: uniqueValues(params.getAll("profile")),
    regions: uniqueValues(params.getAll("region")),
    priceMin: numberOrNull(params.get("priceMin")),
    priceMax: numberOrNull(params.get("priceMax")),
    ageMin: numberOrNull(params.get("ageMin")),
    ageMax: numberOrNull(params.get("ageMax")),
    scoreMin: numberOrNull(params.get("scoreMin")),
    scoreMax: numberOrNull(params.get("scoreMax")),
    availableOnly: params.get("available") === "1",
    groupByOutturn: params.get("group") !== "0",
    sort: VALID_SORTS.has(sort) ? sort : "catalogue",
    view: params.get("view") === "gallery" ? "gallery" : "list",
  };
}

export function serializeQuery(query) {
  const params = new URLSearchParams();
  if (query.q?.trim()) params.set("q", query.q.trim());
  for (const profile of uniqueValues(query.profiles ?? [])) params.append("profile", profile);
  for (const region of uniqueValues(query.regions ?? [])) params.append("region", region);
  for (const [key, value] of [
    ["priceMin", query.priceMin], ["priceMax", query.priceMax],
    ["ageMin", query.ageMin], ["ageMax", query.ageMax],
    ["scoreMin", query.scoreMin], ["scoreMax", query.scoreMax],
  ]) {
    if (numberOrNull(value) != null) params.set(key, String(Number(value)));
  }
  if (query.availableOnly) params.set("available", "1");
  if (query.groupByOutturn === false) params.set("group", "0");
  if (VALID_SORTS.has(query.sort) && query.sort !== "catalogue") params.set("sort", query.sort);
  if (query.view === "gallery") params.set("view", "gallery");
  const value = params.toString();
  return value ? `?${value}` : "";
}

function numericValue(record, field) {
  const source = field === "review" ? record.reviewScore : record[field];
  const cleaned = String(source ?? "").replace(/[^\d.-]+/g, "");
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

function within(value, minimum, maximum) {
  if (minimum == null && maximum == null) return true;
  if (value == null) return false;
  return (minimum == null || value >= minimum) && (maximum == null || value <= maximum);
}

function isAvailable(availability, code) {
  const entry = availability?.get?.(code) ?? availability?.[code];
  if (entry && typeof entry === "object") {
    if (entry.status) return entry.status === "in_stock" || entry.status === "limited_stock";
    return Boolean(entry.available ?? entry.inStock);
  }
  return Boolean(entry);
}

const fold = (value) => String(value ?? "").trim().toLocaleLowerCase();

export function filterAndSort(records, query, availability = new Map()) {
  const search = fold(query.q);
  const profiles = new Set((query.profiles ?? []).map(fold));
  const regions = new Set((query.regions ?? []).map(fold));
  const filtered = records.filter((record) => {
    if (search && ![
      record.code, record.name, record.distillery, record.flavourProfile,
      record.region, record.caskType, record.outturn,
    ].some((value) => fold(value).includes(search))) return false;
    if (profiles.size && !profiles.has(fold(record.flavourProfile))) return false;
    if (regions.size && !regions.has(fold(record.region))) return false;
    if (!within(numericValue(record, "price"), query.priceMin, query.priceMax)) return false;
    if (!within(numericValue(record, "age"), query.ageMin, query.ageMax)) return false;
    if (!within(numericValue(record, "review"), query.scoreMin, query.scoreMax)) return false;
    if (query.availableOnly && !isAvailable(availability, record.code)) return false;
    return true;
  });

  if (!query.sort || query.sort === "catalogue") return filtered;
  const separator = query.sort.lastIndexOf("-");
  const field = query.sort.slice(0, separator);
  const direction = query.sort.slice(separator + 1) === "desc" ? -1 : 1;
  const key = { profile: "flavourProfile", review: "reviewScore" }[field] ?? field;
  const numeric = new Set(["review", "price", "abv", "age"]);
  return filtered.map((record, index) => ({ record, index })).sort((left, right) => {
    const a = numeric.has(field) ? numericValue(left.record, field) : fold(left.record[key]);
    const b = numeric.has(field) ? numericValue(right.record, field) : fold(right.record[key]);
    if (a == null && b == null) return left.index - right.index;
    if (a == null) return 1;
    if (b == null) return -1;
    const comparison = numeric.has(field)
      ? a - b
      : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
    return comparison === 0 ? left.index - right.index : comparison * direction;
  }).map(({ record }) => record);
}
